import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { inject, Injectable } from '@angular/core';
import {
  AuthorizationService,
  DropdownAction,
  RestService,
  SettingsService,
  StandardActionBuilder,
} from '@infodata-group/inf-commons';
import {
  CARTOGRAPHY_FOLDER_ID,
  CompanyParams,
} from '@monorepo-projects/alwayson-shared';
import { Observable, Observer, of, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Tools } from '../engine/tools/tools';
import { CompanyPresetEventTypeEnum } from '../enum/company-preset-event-type.enum';
import { StandardMenuItemFactory } from '../factory/standard-menu-item.factory';
import { CompanyPreset } from '../model/preset/company-preset';
import { StandardMenuItem } from '../model/standard-menu-item';
import { PresetService } from './preset.service';

const logger = getInfCLogger('StandardMenuService');

@Injectable({
  providedIn: 'root',
})
export class StandardMenuService {
  protected readonly restService = inject(RestService);
  protected readonly authorizationService = inject(AuthorizationService);
  protected readonly settingsService = inject(SettingsService);
  protected readonly companyPresetService = inject(PresetService);

  public items: StandardMenuItem[] = [];

  public isLoaded = false;
  protected emitter$ = new Subject<boolean>();
  public currentItem: StandardMenuItem;
  public index: number[] = [];

  private companyPreset: CompanyPreset;

  constructor() {
    this.subscribeToCompanyPresetService();
  }
  /**
   * Load menu & return observable
   */
  public initMenu(): void {
    if (!this.authorizationService.userContext) {
      this.authorizationService.logout(true);
      return;
    }
    this.loadMenu$().subscribe({
      next: (item: StandardMenuItem) => {
        this.items = item.submenus;
        CompanyParams.hasAccessToMat =
          item.submenus.find((i) => i.id === 'FOLDER-MATERIAL') !== undefined;
        this.updateCartographyMenu();
        this.isLoaded = true;
        this.emitter$.next(true);
      },
      error: (e) => {
        logger.error(`Impossible to load menu`, { error: e, userContext: this.authorizationService.userContext });
        this.authorizationService.logout(true);
      },
    });
  }
  private findMultipleByUrl(url: string): StandardMenuItem[] {
    url = url[0] !== '/' ? `/${url}` : url;
    const result = this.items
      .map((x) => x.isMatchingRecursive(url, 'route', []))
      .filter((x) => x.length > 0);
    if (result.length !== 1) {
      return [];
    }
    return result[0];
  }
  public findByUrl(url: string): StandardMenuItem | undefined {
    const result = this.findMultipleByUrl(url);
    if (result.length === 0) {
      return undefined;
    }
    return result.at(-1);
  }
  public getFirstItem(): StandardMenuItem[] {
    for (const item of this.items) {
      if (item.route) {
        return [item];
      }
      if (item.hasSubMenus()) {
        for (const child of item.submenus) {
          if (child.route) {
            return [item, child];
          }
        }
      }
    }
    return [];
  }
  public isAllowed(url: string) {
    return this.findMultipleByUrl(Tools.cleanUrl(url)).length > 0;
  }
  protected loadMenu$(): Observable<StandardMenuItem> {
    return this.restService
      .get(new StandardMenuItemFactory('menu-temp/'), {
        isSilent: true,
        showLoading: false,
      })
      .pipe(
        map((items) => {
          const cartographyMenu = items.submenus.find(
            (i) => i.id === CARTOGRAPHY_FOLDER_ID,
          );
          if (cartographyMenu) {
            cartographyMenu.actions = [
              new DropdownAction(
                'preset',
                'ellipsis-v',
                of([
                  new StandardActionBuilder(
                    'create-preset',
                    'wand-magic-sparkles',
                  )
                    .setText('i18n.common.create-preset')
                    .build(),
                  new StandardActionBuilder('export-preset', 'upload')
                    .setText('i18n.common.export-preset')
                    .build(),
                  new StandardActionBuilder('import-preset', 'download')
                    .setText('i18n.common.import-preset')
                    .build(),
                ]),
                '',
              ),
            ];
          }
          return items;
        }),
      );
  }
  public subscribe(observer: Partial<Observer<boolean>>): Subscription {
    return this.emitter$.subscribe(observer);
  }
  public getTreeMenu(value: string, key: string): StandardMenuItem[] {
    for (const item of this.items) {
      const r = item.isMatchingRecursive(value, key, []);
      if (r.length > 0) {
        return r;
      }
    }
    return [];
  }
  private subscribeToCompanyPresetService() {
    this.companyPresetService.subscribe({
      next: (event) => {
        switch (event.type) {
          case CompanyPresetEventTypeEnum.currentCompanyPresetSelected:
          case CompanyPresetEventTypeEnum.currentCompanyPresetChange:
            this.companyPreset = event.value;
            this.updateCartographyMenu();
            break;
        }
      },
    });
  }
  private updateCartographyMenu(): void {
    if (this.items.length === 0) {
      return;
    }
    const folderCartography = this.items.find(
      (i) => i.id === CARTOGRAPHY_FOLDER_ID,
    );
    if (!folderCartography) {
      return;
    }
    if (this.companyPreset) {
      folderCartography.submenus = this.companyPreset.folders.map((f) =>
        f.toGenericMenuItem(),
      );
    }
    folderCartography.submenus.unshift(
      new StandardMenuItem(
        'CARTOGRAPHY-PATH',
        'Trajet',
        '',
        'cartography/path',
      ),
    );
    folderCartography.submenus.unshift(
      new StandardMenuItem(
        'CARTOGRAPHY-SEARCH',
        'Recherche',
        '',
        'cartography/search',
      ),
    );
  }

  public clear() {
    this.isLoaded = false;
    this.items = [];
    this.companyPreset = null;
    this.currentItem = null;
  }

  public setIndexByUrl(url: string) {
    const index: number[] = [];
    this.index = this.findIndexRecursive(this.items, url, index);
    this.currentItem = this.findByUrl(url);
  }

  private findIndexRecursive(
    items: StandardMenuItem[],
    url: string,
    index: number[],
  ): number[] | null {
    let foundPath: number[] | null = null;
    items.forEach((newItem, i) => {
      if (newItem.route === url) {
        foundPath = [...index, i];
        return;
      }
      if (newItem.submenus && !foundPath) {
        const result = this.findIndexRecursive(newItem.submenus, url, [
          ...index,
          i,
        ]);
        if (result) {
          foundPath = result;
          return;
        }
      }
    });
    return foundPath;
  }
}
